import { DateTime } from 'luxon';
import { uncompactNumber } from '@/utilities/Number';
import { DirectiveBinding } from 'vue';

const APPTABLE = 'apptable';
const SORTABLE = 'sortable';
const DESC = 'descending';
const ASC = 'ascending';
const TEXT_RIGHT = 'text-right';
const SORTED = 'sorted';
const NO_SORT = 'nosort';
const ACTIONS = 'actions';

enum elementType {
  money = 'money',
  compactmoney = 'compactmoney',
  number = 'number',
  percent = 'percent',
  date = 'date',
  dataindex = 'dataindex',
  child = 'child',
  yearspan = 'yearspan'
}

const elementTypes = Object.values(elementType);

const numberFromInnerText = (text: string): number => {
  return numberFromString(text.replace(/[$,%]/g, ''));
};

const numberFromString = (str: string | undefined): number => {
  const n = Number(str || '0');
  return Number.isNaN(n) ? 0 : n;
};

const comparer = (column: number, direction: string, type?: elementType) => (rowA: Element, rowB: Element): number => {
  const [aa, bb] = direction === DESC ? [rowB, rowA] : [rowA, rowB];
  const aindex = (aa.children[column] as HTMLElement).dataset.index;
  const bindex = (bb.children[column] as HTMLElement).dataset.index;
  const a = (aa.children[column] as HTMLElement).innerText;
  const b = (bb.children[column] as HTMLElement).innerText;

  let result;
  switch (type) {
    case elementType.compactmoney:
      result = uncompactNumber(a) - uncompactNumber(b);
      break;
    case elementType.money:
    case elementType.number:
    case elementType.percent:
      if(a === 'Statutory') {
        result = 1
      } else if(b === 'Statutory') {
        result = -1
      } else {
        result = numberFromInnerText(a) - numberFromInnerText(b);
      }
      break;
    case elementType.dataindex:
      result = numberFromString(aindex) - numberFromString(bindex);
      break;
    case elementType.date:
      // DateTime.fromFormat('--', 'M/d/yyyy') results in NaN; '--' is possible if passed through the dashempty filter
      // subtraction with a NaN results in a NaN, causing the sort to misbehave
      result = (() => {
        const toMillis = (dateStr: string) : number => {
          let millis = DateTime.fromFormat(dateStr, 'M/d/yyyy').toMillis();
          if (isNaN(millis)) {
            // some tables show the date with 2 digit year — e.g. 1/11/22
            millis = DateTime.fromFormat(dateStr, 'M/d/yy').toMillis();
          }
          return isNaN(millis) ? 0 : millis;
        };
        return toMillis(a) - toMillis(b);
      })();
      break;
    case elementType.yearspan:
      // e.g. "2021-2022" or "2021-22"
      result = (() => {
        const normalize = (str: string) : string[] => {
          const arr = str.split('-').map(s => s.trim());
          for (let i = arr.length; i < 2; i++) {
            arr.push('0'); // make sure str is split into 2, otherwise fill with zeros
          }
          return arr;
        };
        const [years1, years2] = [normalize(a), normalize(b)];

        let res = numberFromString(years1[0]) - numberFromString(years2[0]);
        if (res === 0) {
          res = numberFromString(years1[1]) - numberFromString(years2[1]);
        }
        return res;
      })();
      break;
    default:
      result = ('' + a).localeCompare(('' + b));
  }
  return result;
};

const columnFromElement = (td: HTMLElement): number => {
  return Array.from(td?.parentNode?.children || []).indexOf(td);
};

const directionFromElement = (td: HTMLElement): string => {
  return td.classList.contains(DESC) ? ASC : td.classList.contains(ASC) ? DESC : ASC;
};

const typeFromElement = (td: HTMLElement): elementType | undefined => {
  const type = elementTypes.find(t => td.classList.contains(t));
  if (!type) {
    return td?.classList.contains(TEXT_RIGHT) ? elementType.number : undefined;
  }
  return type;
};

function elementIsChild(element: Element): boolean {
  return element.className.split(' ').includes(elementType.child);
}

const tdFromTarget = (target: HTMLElement): HTMLElement | null => {
  if (target.nodeName.toLowerCase() !== 'td') {
    return target.closest('td');
  } else {
    return target;
  }
};

const clickHandler = (table: Element) => (e: Event): void => {
  e.stopPropagation();

  const target = e.target as HTMLElement;
  const thead = table.querySelector('thead');
  const tbody = table.querySelector('tbody');
  const td = tdFromTarget(target);
  // validations
  if (!target || !thead || !tbody || !td || !td.parentNode) {
    return;
  }
  if (!td.closest('thead')) {
    return;
  }
  if (td.classList.contains(NO_SORT) || td.classList.contains(ACTIONS)) {
    return;
  }
  // table body manipulations
  const direction = directionFromElement(td);
  const frozen = Array.from(table.querySelectorAll('tbody > tr.frozen'));
  const rows = Array.from(table.querySelectorAll('tbody > tr:not(.frozen)'));
  const parentIndexes = rows
    .map((element, index) => ({element, index}))
    .filter(x => !elementIsChild(x.element));
  const mapped = parentIndexes.map(x => {
    const children: Element[] = [];
    for (let i = x.index + 1; i < rows.length; i++) {
      if (!elementIsChild(rows[i])) {
        break;
      }
      children.push(rows[i]);
    }
    return {element: x.element, children};
  });

  const sorted = rows.filter(x => !elementIsChild(x)).sort(comparer(columnFromElement(td), direction, typeFromElement(td)));

  // add children
  mapped.forEach(row => {
    const parentIndex = sorted.findIndex((v) => v === row.element);
    sorted.splice(parentIndex + 1, 0, ...row.children);
  });

  [...frozen, ...sorted].forEach(tr => tbody.appendChild(tr));

  // table header manipulations
  thead.querySelectorAll('tr > td').forEach(cell => cell.classList.remove(SORTED, DESC, ASC));
  td.classList.add(SORTED, direction);
};

export const DirectiveFunction = (el: HTMLElement, binding: DirectiveBinding): void => {
  if (binding.value !== false) {
    el.classList.add(APPTABLE, SORTABLE);
    if (!el.dataset.tablesort) { // prevent duplicate listeners
      const thead = el.querySelector('thead')
      if (thead) {
        el.dataset.tablesort = 'listening';
        thead.addEventListener('click', clickHandler(el));
      }
    }
  }
};
